import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { escalateToVendor, type SupportContact, type TicketEscalationApp } from '../api/businessApps'
import { ApiError } from '../api/client'
import type { Option, VendorCase } from '../api/vendorCases'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'

/**
 * Fereastra "Escalate to <furnizor> support" de pe tichet.
 *
 * Emailul vine pregatit de server (`draft`) si se poate schimba in intregime.
 * NIMIC nu pleaca fara clic: "Send" trimite pe drumul conversatiei tichetului
 * (raspunsul furnizorului se intoarce in acelasi tichet), "Open in mail client"
 * deschide un `mailto:` cu acelasi text, iar "Record as sent" scrie numai urma
 * (comentariul intern + termenul) cand emailul a plecat din clientul de mail.
 *
 * Cu `nextOf` (un caz deschis), fereastra duce cazul la nivelul urmator:
 * contactul de pe nivel e ales, iar emailul pleaca cu `case_id` - ramane in
 * acelasi caz.
 *
 * Bifa "Mark ticket as External escalated" (escaladarea Easeedesk de felul 3,
 * "Waiting for Third Party") apare cand omul are dreptul butonului din tichet.
 * La un caz NOU vine bifata - cazul la furnizor e o escaladare externa, si
 * tichetul trebuie sa se vada asa in lista si la filtrul "External escalated";
 * la nivelul urmator vine nebifata. Se trimite mereu, bifata sau nu (`0` =
 * refuzata anume): lipsa ei inseamna pe server "escaladeaza daca se poate".
 * Furnizorul extern pleaca ghicit dupa numele suportului; nealeas (0), serverul
 * il cauta dupa nume sau face unul nou. Motivul pleaca pe primul din setari.
 */

const TITLE = 'Vendor escalation'

/** Contactele pe nivel (1, 2, 3...) intai, apoi ceilalti. */
export function escalationPath(contacts: SupportContact[]): { path: SupportContact[]; others: SupportContact[] } {
  const path = contacts.filter((c) => c.level > 0).sort((a, b) => a.level - b.level)
  const others = contacts.filter((c) => c.level <= 0)
  return { path, others }
}

export function contactLabel(c: SupportContact): string {
  const who = c.role !== '' ? `${c.name} (${c.role})` : c.name
  return c.level > 0 ? `Level ${c.level} - ${who}` : who
}

/** `mailto:` cu destinatarii, Cc, subiectul si corpul, toate codate. */
export function mailtoHref(to: string, cc: string, subject: string, body: string): string {
  const recipients = to
    .split(/[;,\s]+/)
    .filter((a) => a !== '')
    .map((a) => encodeURIComponent(a))
    .join(',')
  const params = [`subject=${encodeURIComponent(subject)}`, `body=${encodeURIComponent(body)}`]
  if (cc.trim() !== '') params.unshift(`cc=${encodeURIComponent(cc.trim())}`)
  return `mailto:${recipients}?${params.join('&')}`
}

/** Furnizorul extern cu numele cel mai apropiat de al suportului aplicatiei; 0 cand nu e unul clar. */
export function guessProvider(providers: Option[], supportName: string): number {
  const name = supportName.trim().toLowerCase()
  if (name === '') return 0
  const hit =
    providers.find((p) => p.name.trim().toLowerCase() === name) ??
    providers.find((p) => {
      const other = p.name.trim().toLowerCase()
      return other !== '' && (other.includes(name) || name.includes(other))
    })
  return hit?.value ?? 0
}

/** Primul paragraf al emailului catre nivelul urmator. */
export function nextLevelIntro(c: VendorCase): string {
  const ref = c.vendorRef !== '' ? ` (your case ${c.vendorRef})` : ''
  return c.awaitingResponse
    ? `We escalated this on ${c.sentAt}${ref} and have not had an answer yet.`
    : `We escalated this on ${c.sentAt}${ref} and need your help to move it forward.`
}

export function VendorEscalateModal({
  ticketId,
  app,
  open,
  onClose,
  nextOf,
  waiting,
}: {
  ticketId: number
  app: TicketEscalationApp
  open: boolean
  onClose(): void
  nextOf?: VendorCase
  waiting?: { providers: Option[]; reasons: Option[] }
}) {
  const queryClient = useQueryClient()
  const { path, others } = useMemo(() => escalationPath(app.contacts), [app.contacts])
  const preset = nextOf?.nextLevel ? app.contacts.find((c) => c.id === nextOf.nextLevel?.contactId) : undefined
  const first = preset ?? path[0] ?? others.find((c) => c.email !== '') ?? null

  const [contactId, setContactId] = useState<number>(first?.id ?? 0)
  const [to, setTo] = useState(first?.email ?? '')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState(app.draft.subject)
  const [body, setBody] = useState(() => {
    if (!nextOf) return app.draft.body
    const lines = app.draft.body.split('\n')
    return lines[0] === 'Hello,'
      ? [lines[0], '', nextLevelIntro(nextOf), ...lines.slice(1)].join('\n')
      : `${nextLevelIntro(nextOf)}\n\n${app.draft.body}`
  })
  const [noSettings, setNoSettings] = useState(false)
  const offerWait = waiting !== undefined && !(nextOf?.ticketWaiting ?? false)
  const noReasons = waiting !== undefined && waiting.reasons.length === 0
  const [wait, setWait] = useState(() => offerWait && !nextOf && !noReasons)
  const [provider, setProvider] = useState(() => (waiting ? guessProvider(waiting.providers, app.supportName) : 0))
  const [reason, setReason] = useState(() => (waiting && waiting.reasons.length > 0 ? waiting.reasons[0].value : 0))

  const contact = app.contacts.find((c) => c.id === contactId) ?? null

  const choose = (id: number) => {
    setContactId(id)
    const picked = app.contacts.find((c) => c.id === id)
    if (picked && picked.email !== '') setTo(picked.email)
  }

  const send = useMutation({
    mutationFn: (mode: 'send' | 'record') =>
      escalateToVendor(ticketId, {
        app_id: app.id,
        contact_id: contactId,
        to,
        cc,
        subject,
        body,
        mode,
        ...(nextOf ? { case_id: nextOf.id } : {}),
        ...(offerWait ? (wait ? { wait_third_party: '1', external_provider: provider, escalation_reason: reason } : { wait_third_party: '0' }) : {}),
      }),
    onSuccess: (answer) => {
      toast(answer.message, TITLE)
      /* Tichetul poate fi acum "External escalated": fisa, fila Change si lista. */
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] })
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
      void queryClient.invalidateQueries({ queryKey: ['business-apps', 'ticket', ticketId] })
      void queryClient.invalidateQueries({ queryKey: ['business-apps', 'vendor-cases'] })
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'conversation'] })
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'comments'] })
      void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'changes'] })
      onClose()
    },
    onError: (error) => {
      const details = error instanceof ApiError ? error.details : undefined
      if (details?.error === 'no_settings') setNoSettings(true)
      toast(error instanceof ApiError && error.message ? error.message : 'The escalation could not be sent.', TITLE, 'error')
    },
  })

  const support = app.support
  const basis = app.dueBasis === 'support_hours' ? 'within support hours' : app.dueBasis === 'calendar' ? 'calendar hours' : ''
  const busy = send.isPending
  const waitReady = !(offerWait && wait) || reason > 0
  const ready = to.trim() !== '' && subject.trim() !== '' && body.trim() !== '' && waitReady

  return (
    <Modal
      id={`vendorEscalate-${ticketId}-${app.id}`}
      open={open}
      onClose={onClose}
      large
      portal
      title={nextOf?.nextLevel ? `Escalate to level ${nextOf.nextLevel.level} - ${app.supportName}` : `Escalate to ${app.supportName} support`}
      footer={
        <div className="vx-footer">
          <a className="btn btn-default vx-mailto" href={mailtoHref(to, cc, subject, body)}>
            <i className="fas fa-envelope-open-text" aria-hidden="true"></i> Open in mail client
          </a>
          <button type="button" className="btn btn-default vx-record" disabled={busy || !ready} onClick={() => send.mutate('record')}>
            Record as sent
          </button>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-theme vx-send" disabled={busy || !ready} onClick={() => send.mutate('send')}>
            <i className="fas fa-paper-plane" aria-hidden="true"></i> Send
          </button>
        </div>
      }
    >
      <div className="vx-modal">
        <div className="vx-app">
          <strong>{app.name}</strong>
          {app.version !== '' && <span className="text-muted"> {app.version}</span>}
          {app.criticalityLabel !== '' && <span className="label label-default vx-crit">{app.criticalityLabel}</span>}
          <div className="vx-facts">
            {support.scheduleLabel !== '' && <span>Support: {support.scheduleLabel}</span>}
            {support.responseHours !== null && <span>Response time: {support.responseHours}h</span>}
            {support.contractNo !== '' && <span>Contract: {support.contractNo}</span>}
            {support.portalUrl !== '' && (
              <span>
                <a href={support.portalUrl} target="_blank" rel="noopener noreferrer">
                  Vendor ticket portal <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                </a>
              </span>
            )}
          </div>
          {app.components.length > 0 && (
            <div className="vx-components">
              On this ticket:{' '}
              {app.components.map((c, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <strong>{c.name}</strong> ({c.roleLabel}
                  {c.note !== '' ? `, ${c.note}` : ''})
                </span>
              ))}
            </div>
          )}
        </div>

        {noSettings && (
          <div className="alert alert-warning vx-no-settings">
            The portal cannot send email for this service provider (conversation settings are missing). Use{' '}
            <strong>Open in mail client</strong>, then <strong>Record as sent</strong>.
          </div>
        )}

        <div className="form-horizontal">
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor={`vx-contact-${ticketId}`}>
              Contact
            </label>
            <div className="col-sm-10">
              <select id={`vx-contact-${ticketId}`} className="form-control vx-contact" value={contactId} onChange={(ev) => choose(Number(ev.target.value))}>
                <option value={0}>- write the address yourself -</option>
                {path.length > 0 && (
                  <optgroup label="Escalation path">
                    {path.map((c) => (
                      <option key={c.id} value={c.id}>
                        {contactLabel(c)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {others.length > 0 && (
                  <optgroup label="Other contacts">
                    {others.map((c) => (
                      <option key={c.id} value={c.id}>
                        {contactLabel(c)}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {contact && (
                <p className="help-block vx-contact-info">
                  {contact.phone !== '' && (
                    <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}>
                      <i className="fas fa-phone" aria-hidden="true"></i> {contact.phone}
                    </a>
                  )}
                  {contact.levelNote !== '' && <span className="vx-level-note"> {contact.levelNote}</span>}
                </p>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor={`vx-to-${ticketId}`}>
              To
            </label>
            <div className="col-sm-10">
              <input id={`vx-to-${ticketId}`} className="form-control vx-to" value={to} onChange={(ev) => setTo(ev.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor={`vx-cc-${ticketId}`}>
              Cc
            </label>
            <div className="col-sm-10">
              <input id={`vx-cc-${ticketId}`} className="form-control vx-cc" value={cc} onChange={(ev) => setCc(ev.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor={`vx-subject-${ticketId}`}>
              Subject
            </label>
            <div className="col-sm-10">
              <input id={`vx-subject-${ticketId}`} className="form-control vx-subject" value={subject} onChange={(ev) => setSubject(ev.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-2 control-label" htmlFor={`vx-body-${ticketId}`}>
              Message
            </label>
            <div className="col-sm-10">
              <textarea id={`vx-body-${ticketId}`} className="form-control vx-body" rows={14} value={body} onChange={(ev) => setBody(ev.target.value)} />
            </div>
          </div>
        </div>

        {offerWait && waiting && (
          <div className="vx-wait">
            <label className="vx-wait-check">
              <input type="checkbox" checked={wait} disabled={noReasons} onChange={(ev) => setWait(ev.target.checked)} /> Mark ticket as{' '}
              <strong>External escalated</strong> (Waiting for Third Party){' '}
              <span className="text-muted">(stops the SLA clock; the ticket can come back when the vendor case closes)</span>
            </label>
            {noReasons && <span className="text-danger vx-wait-noreason">No third party escalation reasons are set up (Settings &gt; Easeedesk).</span>}
            {wait && (
              <div className="vx-wait-fields">
                <select
                  className="form-control input-sm vx-wait-provider"
                  aria-label="External provider"
                  value={provider}
                  onChange={(ev) => setProvider(Number(ev.target.value))}
                >
                  <option value={0}>New external provider: {app.supportName}</option>
                  {waiting.providers.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <select className="form-control input-sm vx-wait-reason" aria-label="Escalation reason" value={reason} onChange={(ev) => setReason(Number(ev.target.value))}>
                  <option value={0}>- reason -</option>
                  {waiting.reasons.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        <p className="vx-due">
          {app.dueIfNow !== null ? (
            <>
              Response due if sent now: <strong>{app.dueIfNow}</strong> ({basis})
            </>
          ) : (
            'No response time in the contract.'
          )}
        </p>
      </div>
    </Modal>
  )
}
